import { Component, OnInit, OnDestroy, Inject, PLATFORM_ID } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, debounceTime, distinctUntilChanged, takeUntil } from 'rxjs';
import { ApiResponse } from '../../../_models/api-response.model';
import { AgentAdminService } from '../../../_services/api/agent-admin.service';
import { ExcelExportService } from '../../../_services/app/excel-export.service'; 
import { AdminAgentCard } from '../admin-agent-card/admin-agent-card';
import { AgentList, AgentSearchCriteria } from '../../../_models/agent.model';
import { PaginationComponent } from '../../../_shared_components/pagination/pagination.component';

@Component({
  selector: 'app-admin-agents-list',
  imports: [CommonModule, FormsModule, AdminAgentCard, PaginationComponent],
  templateUrl: './admin-agents-list.html',
  styleUrl: './admin-agents-list.scss',
})
export class AdminAgentsList implements OnInit, OnDestroy {
  showFilterPopup: boolean = false;
  selectedCategory: string = 'all';

  agents: AgentList[] = [];
  isLoading = false;
  currentPage = 0;
  pageSize = 10;
  totalCount = 0;
  totalPages = 0;
  
  searchTerm = '';
  private searchSubject = new Subject<string>();
  selectedVisibility: string = 'all';
  selectedAvailability: string = 'all';
  selectedSort: string = 'createdAt,desc';
  private destroy$ = new Subject<void>();

  constructor(
    private agentService: AgentAdminService, 
    private excelExportService: ExcelExportService, 
    private router: Router,
    private route: ActivatedRoute,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {}

  ngOnInit(): void {
    this.setupSearch();
    this.loadAgents();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setupSearch(): void {
    this.searchSubject.pipe(
      debounceTime(500),
      distinctUntilChanged(),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.currentPage = 0;
      this.loadAgents();
    });
  }

  onSearch(): void {
    this.searchSubject.next(this.searchTerm);
  }

  onFilterChange(): void {
    this.currentPage = 0;
    this.loadAgents();
  }

  onSortChange(): void {
    this.currentPage = 0;
    this.loadAgents();
  }

  loadAgents(): void {
    this.isLoading = true;
    
    const criteria: AgentSearchCriteria = {};
    if (this.searchTerm.trim()) {
      criteria.search = this.searchTerm.trim();
    }

    if (this.selectedVisibility !== 'all') {
      criteria.isEnabled = this.selectedVisibility === 'true';
    }

    if (this.selectedAvailability !== 'all') {
      criteria.isAvailable = this.selectedAvailability === 'true';
    }

    const [sortField, sortDirection] = this.selectedSort.split(',');
    this.agentService.getAllAgents(criteria, this.currentPage, this.pageSize, sortField, sortDirection)
      .subscribe({
        next: (response: ApiResponse<any>) => {
          this.agents = response.data?.content || [];
          this.totalCount = response.data?.totalElements || 0;
          this.totalPages = response.data?.totalPages || 0;
          setTimeout(() => {
            this.isLoading = false;
          }, 500);
        },
        error: (error) => {
          console.error('Error loading agents:', error);
          this.isLoading = false;
        }
      });
  }

  onAgentUpdated(): void {
    this.loadAgents();
  }

  onAgentDeleted(): void {
    this.loadAgents();
  }

  onPageChange(page: number) {
    this.isLoading = true;
    this.currentPage = page;
    if (isPlatformBrowser(this.platformId)) {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    setTimeout(() => {
      this.loadAgents();
    }, 1000);
  }

  toggleFilterPopup() {
    this.showFilterPopup = !this.showFilterPopup;
  }

  closeFilterPopup(event: Event) {
    if ((event.target as HTMLElement).classList.contains('filter-overlay')) {
      this.showFilterPopup = false;
    }
  }

  exportAgents(): void {
    this.isLoading = true;
    const criteria: AgentSearchCriteria = {};
    if (this.searchTerm.trim()) {
      criteria.search = this.searchTerm.trim();
    }

    if (this.selectedVisibility !== 'all') {
      criteria.isEnabled = this.selectedVisibility === 'true';
    }

    this.agentService.getAllAgents(criteria, 0, 1000)
      .subscribe({
        next: (response: ApiResponse<any>) => {
          const allAgents = response.data?.content || [];
          this.generateExcelExport(allAgents);
          this.isLoading = false;
        },
        error: (error) => {
          console.error('Error loading agents for export:', error);
          this.isLoading = false;
          alert('Erreur lors du chargement des agents pour l\'exportation');
        }
      });
  }

  private generateExcelExport(agents: AgentList[]): void {
    try {
      const fileName = this.excelExportService.generateFileName();
      const exportDate = new Date().toLocaleDateString('fr-BE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
      
      const title = `Liste des agents Farco Security - Export du ${exportDate} (${agents.length} membres)`;
      //this.excelExportService.exportMembersToExcel(agents, fileName, title);
    } catch (error) {
      console.error('Error generating Excel export:', error);
      alert('Erreur lors de la génération du fichier Excel');
    }
  }

  addAgent(){
    this.router.navigate(['/admin/agents/create']);
  }
}